import React from "react";

function Pulse({ className = "" }) {
  return <div className={`animate-pulse bg-black/10 ${className}`} />;
}

export function DashboardSkeleton() {
  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-skeleton">
      {/* Header skeleton */}
      <div className="h-20 border-b-2 border-black bg-white flex items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <Pulse className="w-10 h-10" />
          <Pulse className="w-28 h-5 hidden sm:block" />
        </div>
        <div className="flex items-center gap-3">
          <Pulse className="w-9 h-9" />
          <Pulse className="w-10 h-10" />
        </div>
      </div>

      <div className="flex max-w-[1600px] mx-auto">
        {/* Sidebar skeleton (desktop) */}
        <aside className="hidden md:flex flex-col w-60 border-r-2 border-black">
          <div className="p-4 border-b border-black bg-[#F0F0F0]">
            <Pulse className="w-24 h-3 mb-2" />
            <Pulse className="w-32 h-5" />
          </div>
          <div className="flex-1 space-y-0">
            {[1, 2, 3].map((i) => (
              <div key={i} className="px-4 py-3 border-b border-black/10">
                <Pulse className="w-full h-4 mb-1" />
                <Pulse className="w-16 h-3" />
              </div>
            ))}
          </div>
        </aside>

        {/* Main content skeleton */}
        <main className="flex-1 px-6 md:px-10 py-10">
          <div className="mb-10">
            <Pulse className="w-20 h-3 mb-3" />
            <Pulse className="w-64 h-12 mb-2" />
            <Pulse className="w-48 h-4" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="border-2 border-black/20">
                <Pulse className="h-32 border-b border-black/20" />
                <div className="p-5 space-y-3">
                  <Pulse className="w-12 h-3" />
                  <Pulse className="w-full h-6" />
                  <Pulse className="w-3/4 h-3" />
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}

export function BoardViewSkeleton() {
  return (
    <div className="h-[100dvh] flex flex-col bg-white overflow-hidden" data-testid="board-skeleton">
      {/* Header skeleton */}
      <div className="h-14 md:h-20 border-b-2 border-black bg-white flex items-center justify-between px-3 md:px-6">
        <div className="flex items-center gap-3">
          <Pulse className="w-10 h-10" />
          <Pulse className="w-28 h-5 hidden sm:block" />
        </div>
        <div className="flex items-center gap-3">
          <Pulse className="w-9 h-9 md:w-24" />
          <Pulse className="w-9 h-9 md:w-28" />
        </div>
      </div>

      {/* Board info bar skeleton */}
      <div className="border-b border-black bg-[#F9F9F9] px-3 md:px-6 py-2 md:py-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
          <Pulse className="w-12 h-3" />
          <Pulse className="w-32 md:w-40 h-7" />
          <Pulse className="w-14 h-5" />
          <div className="hidden md:flex gap-1">
            {[1, 2, 3].map((i) => (
              <Pulse key={i} className="w-8 h-8 rounded-full" />
            ))}
          </div>
        </div>
        <div className="flex gap-2 md:gap-4 shrink-0">
          <Pulse className="w-16 h-3" />
          <Pulse className="w-16 h-3" />
        </div>
      </div>

      {/* Kanban columns skeleton */}
      <div className="flex-1 overflow-x-auto bg-[#F9F9F9]">
        <div className="inline-flex h-full min-w-full items-start p-0">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="w-[88vw] max-w-[22rem] md:w-80 md:max-w-none flex-shrink-0 flex flex-col border-r border-black bg-white" style={{ height: "var(--kanban-height, calc(100vh - 144px))" }}>
              {/* List header */}
              <div className="p-2.5 md:p-3 border-b border-black flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Pulse className="w-6 h-3" />
                  <Pulse className="w-24 h-5" />
                  <Pulse className="w-6 h-5" />
                </div>
                <div className="flex gap-1">
                  <Pulse className="w-7 h-7" />
                  <Pulse className="w-7 h-7" />
                </div>
              </div>
              {/* Cards */}
              <div className="flex-1 p-2 space-y-2">
                {Array.from({ length: Math.floor(Math.random() * 3) + 2 }, (_, j) => (
                  <div key={j} className="border border-black/20 p-3 space-y-2">
                    <div className="flex gap-1">
                      <Pulse className="w-12 h-4" />
                      <Pulse className="w-10 h-4" />
                    </div>
                    <Pulse className="w-full h-4" />
                    <Pulse className="w-2/3 h-3" />
                  </div>
                ))}
              </div>
              {/* Add card button */}
              <div className="border-t border-black p-2">
                <Pulse className="w-full h-8" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
